import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {DataSourceService} from '../../api/generated/services/DataSourceService';
import type {DataSourceView} from '../../api/generated/models/DataSourceView';
import type {DataSourceRequest} from '../../api/generated/models/DataSourceRequest';
import type {BridgeStatusView} from '../../api/generated/models/BridgeStatusView';
import {ApiError} from '../../api/generated/core/ApiError';
import {helper} from '../../utils/helper';
import type {RootState} from '../store';
import {fetchContentMasterWorkspaceConfig} from './contentMasterSlice';
import {isDataSourceConfigured} from '../../utils/dataSourceHelpers';

// Carries server-provided `errorCode` (e.g. `data_source_two_factor_required`)
// alongside the human-readable message so the UI can branch by code, not by text.
export interface DataSourceErrorPayload {
  message: string;
  errorCode?: string;
  status?: number;
}

const toErrorPayload = (err: unknown, fallback: string): DataSourceErrorPayload => {
  if (err instanceof ApiError) {
    const body = helper.parseApiErrorBody(err);
    return {
      message: (body && body.error) ? body.error : (err.message || fallback),
      errorCode: body && typeof body.errorCode === 'string' ? body.errorCode : undefined,
      status: err.status,
    };
  }
  return { message: fallback };
};

/** Optional arg for `fetchDataSource`; `force` bypasses the loading guard (e.g. after PUT while update still holds loading). */
export type FetchDataSourceArg = { force?: boolean } | undefined;

export const fetchDataSource = createAsyncThunk<
  DataSourceView,
  FetchDataSourceArg,
  { state: RootState; rejectValue: string }
>(
  'dataSource/fetchDataSource',
  async (_, { dispatch, getState, rejectWithValue }) => {
    // Snapshot BEFORE the request: Wawi Bridge pairing happens outside the web
    // app (inside the exe), so this fetch may be the first moment the app
    // learns the DataSource became usable.
    const wasConfigured = isDataSourceConfigured(getState().dataSource.data);
    try {
      const data = await DataSourceService.getAppDataSourceGet();

      // Mirror the post-save refresh of `updateDataSource`: on the
      // not-configured → configured transition, load the Content Master
      // workspace config - otherwise the CM pages bail out of their product
      // fetch (no `wawiLangReady`). The thunk's own `condition` guard drops
      // duplicates while one request is already in flight.
      if (!wasConfigured && isDataSourceConfigured(data)) {
        const root = getState();
        const hasActiveContentMaster = root.licenses.items.some(
          (s) => s.pluginName === 'content_master' && helper.isSubscriptionActive(s)
        );
        if (hasActiveContentMaster && root.contentMaster.workspaceConfig == null) {
          void dispatch(fetchContentMasterWorkspaceConfig(undefined));
        }
      }

      return data;
    } catch (err) {
      if (err instanceof ApiError) {
        return rejectWithValue(err.message);
      }
      throw err;
    }
  },
  {
    // Drop a duplicate dispatch while a request is already in flight.
    condition: (arg, { getState }) => {
      if (arg?.force) return true;
      return !getState().dataSource.loading;
    },
  }
);

// Live Wawi Bridge agent status (GET /ds/bridge/status). Used as a silent
// background poll while the "Database connection" tab is open, so it must not
// toggle the slice's `loading` flag (see reducer below).
export const fetchBridgeStatus = createAsyncThunk<
  BridgeStatusView,
  void,
  { state: RootState; rejectValue: DataSourceErrorPayload }
>(
  'dataSource/fetchBridgeStatus',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const status = await DataSourceService.getAppDataSourceBridgeStatus();
      // Pairing happens inside the exe. When this poll is the first to see the
      // agent paired, re-fetch the whole DataSource so the "configured" gates
      // (and, via fetchDataSource, the CM workspace config) refresh exactly
      // like after saving a data source in the web form.
      const wasPaired = getState().dataSource.data?.bridge?.paired === true;
      if (!wasPaired && status.paired) {
        void dispatch(fetchDataSource({ force: true }));
      }
      return status;
    } catch (err) {
      return rejectWithValue(toErrorPayload(err, 'Failed to fetch bridge status'));
    }
  }
);

export const updateDataSource = createAsyncThunk<
  DataSourceRequest,
  DataSourceRequest,
  { state: RootState; rejectValue: DataSourceErrorPayload }
>(
  'dataSource/updateDataSource',
  async (dataSourceData, { dispatch, getState, rejectWithValue }) => {
    try {
      await DataSourceService.putAppDataSourceUpdate(dataSourceData);
      await dispatch(fetchDataSource({ force: true })).unwrap();

      // The Content Master workspace config is derived from the active data
      // source. The one-shot session bootstrap only loads it when a DS is
      // already configured at app start, so a DS created/changed later in the
      // session would otherwise leave `workspaceConfig` null - and the Content
      // Master pages bail out of their product fetch (no `wawiLangReady`).
      // Refetch it here (only when an active Content Master subscription
      // exists; the endpoint requires one) so navigating to Content Master
      // loads products immediately.
      const root = getState();
      const hasActiveContentMaster = root.licenses.items.some(
        (s) => s.pluginName === 'content_master' && helper.isSubscriptionActive(s)
      );
      if (hasActiveContentMaster) {
        void dispatch(fetchContentMasterWorkspaceConfig(undefined));
      }

      return dataSourceData;
    } catch (err) {
      return rejectWithValue(toErrorPayload(err, 'Failed to update data source'));
    }
  }
);

export const deleteDataSource = createAsyncThunk<
  void,
  void,
  { rejectValue: DataSourceErrorPayload }
>(
  'dataSource/deleteDataSource',
  async (_, { rejectWithValue }) => {
    try {
      await DataSourceService.postAppDataSourceDelete();
    } catch (err) {
      return rejectWithValue(toErrorPayload(err, 'Failed to delete data source'));
    }
  }
);

const dataSourceSlice = createSlice({
  name: 'dataSource',
  initialState: {
    data: null as DataSourceView | null,
    error: null as string | null,
    loading: false,
    deleting: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataSource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataSource.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchDataSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Silent poll: refresh only the embedded bridge status. `loading` stays
      // untouched so the UI never flashes every 15 s; poll errors are ignored
      // (a 400 `bridge_not_bridge_data_source` / 404 just means the DataSource
      // changed - the next `fetchDataSource` corrects the state).
      .addCase(fetchBridgeStatus.fulfilled, (state, action) => {
        if (state.data) {
          state.data.bridge = action.payload;
        }
      })
      .addCase(updateDataSource.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDataSource.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDataSource.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message ?? null;
      })
      .addCase(deleteDataSource.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteDataSource.fulfilled, (state) => {
        state.deleting = false;
        state.data = null;
      })
      .addCase(deleteDataSource.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload?.message ?? null;
      });
  },
});

export default dataSourceSlice.reducer;
